import { Container, htmlToSlate, RichText } from '@amelio/ui-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useLocation, useParams } from 'react-router-dom';

import ObjectiveDetailHeader from './components/detail/ObjectiveDetailHeader';
import ObjectiveDetailMeasurements from './components/detail/ObjectiveDetailMeasurements';
import ObjectiveDiscussion from './components/detail/ObjectiveDiscussion';
import ObjectiveOverallProgress from './components/detail/ObjectiveOverallProgress';
import ObjectiveSkillsPillarsCard from './components/detail/ObjectiveSkillsPillarsCard';
import PillarVisionModal from './components/detail/PillarVisionModal';
import { ObjectiveLoadingState, ObjectiveMessageState } from './components/ObjectivePageStates';

import type { ObjectivesLocationState } from '@/navigation/objectivesRoutes';

import { useGetUsersByIds } from '@/api/directory';
import { useGetObjectiveById } from '@/api/objectives';
import { BackLink } from '@/components/BackLink';
import {
    DETAIL_SECTION_TITLE,
    GLASS_SURFACE,
    OBJECTIVES_PAGE_LAYOUT,
} from '@/components/objectives/objectiveStyles';
import { ObjectiveType } from '@/constants/ObjectiveType';
import { useObjectivesStepBack } from '@/hooks/objectives/useObjectivesStepBack';
import { isResponseError } from '@/hooks/utils/useError';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { objectiveEditPath, objectivesPath, stepBackState } from '@/navigation/objectivesRoutes';
import i18n from '@/plugins/i18n';
import { getLocalizedText } from '@/utils/localization';
import { objectiveHolderIds } from '@/utils/objectiveHolders';
import { clampPercent } from '@/utils/objectiveProgress';
import { getObjectiveStatus, isPublishedObjective, opensOnItsForm } from '@/utils/objectiveStatus';

export default function ObjectiveDetailPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { state: locationState } = useLocation<ObjectivesLocationState | undefined>();
    const { stepBackOr } = useObjectivesStepBack();
    const { id } = useParams<{ id: string }>();
    // A pillar this objective feeds opens "Notre vision partagée" in place; the pillar page itself
    // stays reachable from the objectives overview.
    const [openPillarId, setOpenPillarId] = useState<string | null>(null);

    const { data: objective, isLoading, error } = useGetObjectiveById(id);
    const openPillar = objective?.pillars.find((pillar) => pillar.id === openPillarId);

    const employeeIds = objective ? objectiveHolderIds(objective) : [];
    const { data: employees = [] } = useGetUsersByIds({
        userIds: employeeIds,
        identifier: 'objective-detail',
    });
    const employeesById = new Map(employees.map((employee) => [employee.id, employee]));

    const isIndividual = objective?.type === ObjectiveType.INDIVIDUAL;
    // Only an individual objective works on competencies; a team one never lists any. The names come
    // with the objective, so its reader needs no access to the competency catalog.
    const competencyNames = isIndividual
        ? (objective?.competencies ?? []).map((competency) => getLocalizedText(competency.name))
        : [];

    // Only a navigation from inside the module says the previous entry is its screen: a history
    // that merely has entries may lead out of the application.
    const goBack = () => stepBackOr({ path: objectivesPath() });

    const isNotFound = isResponseError(error) && error.response?.status === 404;
    const hasLoadFailure = !!error && !isNotFound;

    if (isLoading) {
        return <ObjectiveLoadingState dataTestId={'objective-detail-loader'} />;
    }

    if (hasLoadFailure) {
        return (
            <ObjectiveMessageState
                message={t('objectives.errors.loading')}
                isError
                onBack={goBack}
                backLabel={t('objectives.detail.back')}
                backDataTestId={'objective-detail-back'}
            />
        );
    }

    if (isNotFound || !objective) {
        return (
            <ObjectiveMessageState
                message={t('objectives.detail.notFound')}
                isError={false}
                onBack={goBack}
                backLabel={t('objectives.detail.back')}
                backDataTestId={'objective-detail-back'}
            />
        );
    }

    // A draft's writer lands on its form. Anyone else who sees it reads it here, without the
    // progress and the discussion, which start at publication.
    if (opensOnItsForm(objective)) {
        return (
            <Redirect to={{ pathname: objectiveEditPath(objective.id), state: locationState }} />
        );
    }

    const publishedObjective = isPublishedObjective(objective) ? objective : null;

    return (
        <Container
            className={OBJECTIVES_PAGE_LAYOUT.shell}
            dataTestId={'objective-detail-page'}
        >
            <div className={'obj-detail-stack flex flex-col pt-6 pb-8'}>
                <BackLink
                    onClick={goBack}
                    label={t('objectives.detail.back')}
                    dataTestId={'objective-detail-back'}
                />

                <ObjectiveDetailHeader
                    objective={objective}
                    employeesById={employeesById}
                    onEdit={() =>
                        navigate({
                            path: objectiveEditPath(objective.id),
                            state: stepBackState(),
                        })
                    }
                />

                {publishedObjective && (
                    <ObjectiveOverallProgress
                        progress={clampPercent(publishedObjective.progression)}
                        status={getObjectiveStatus(publishedObjective)}
                    />
                )}

                {/* The prototype frames the description in glass around a white panel. */}
                <div
                    className={`obj-description-frame ${GLASS_SURFACE}`}
                    data-testid={'objective-detail-description'}
                >
                    <div className={'obj-description rounded-[8px] bg-white/90'}>
                        <h3 className={`${DETAIL_SECTION_TITLE} mb-3`}>
                            {t('objectives.detail.description')}
                        </h3>

                        {objective.description ? (
                            // `RichText`'s own typed-text class (`text-base-500`) sets an explicit
                            // color on the editable node, so it doesn't inherit from this wrapper
                            // -- the `className` override targets it directly, while size/weight/
                            // line-height (never set on that node) inherit from this div.
                            <div className={'text-navy text-[14px] leading-relaxed font-medium'}>
                                <RichText
                                    value={htmlToSlate(objective.description)}
                                    readOnly
                                    toolbar={false}
                                    locale={i18n.language}
                                    contentMinHeight={'auto'}
                                    // `[contenteditable]` is Slate's own contractual attribute,
                                    // unlike the library's internal `data-testid="slate-editable"`
                                    // node. `!px-0` drops the editor's typing padding, which would
                                    // indent the read-only text under its heading.
                                    className={'[&_[contenteditable]]:!text-navy !px-0'}
                                    dataTestId={'objective-detail-description-content'}
                                />
                            </div>
                        ) : (
                            <p className={'text-obj-muted'}>
                                {t('objectives.detail.noDescription')}
                            </p>
                        )}
                    </div>
                </div>

                <ObjectiveDetailMeasurements
                    objective={objective}
                    employeesById={employeesById}
                />

                <ObjectiveSkillsPillarsCard
                    pillars={objective.pillars}
                    competencyNames={competencyNames}
                    onPillarClick={setOpenPillarId}
                />

                {publishedObjective && <ObjectiveDiscussion objectiveId={publishedObjective.id} />}
            </div>

            {openPillar && (
                <PillarVisionModal
                    isOpen
                    onClose={() => setOpenPillarId(null)}
                    pillar={openPillar}
                />
            )}
        </Container>
    );
}
